/**
 * App Configuration Constants
 * Centralized configuration flags for the application
 */

/**
 * Enable/disable UAE Pass authentication flow
 * When false: buttons directly navigate to manual registration/login
 * When true: buttons show intermediate screens (UAE Pass options)
 */
export const ENABLE_UAEPASS = false;

/**
 * Centralized path for where "go to sign in" redirects should land.
 * - When UAE Pass is disabled: plain welcome screen
 * - When UAE Pass is enabled: welcome screen in LOGIN state
 *
 * All automatic redirections after logout/session-expire/etc. should use this
 * instead of pushing `/login` directly, so both UAE Pass and manual login
 * options are visible when applicable.
 */
// NOTE: The `state` value must match the string value of RegistrationState.LOGIN,
// which is currently `'login'` (lowercase). Do NOT change case without updating
// the enum in `modules/welcome/components/WelcomeScreen.tsx`.
export const WELCOME_LOGIN_PATH = ENABLE_UAEPASS ? '/welcome?state=login' : '/welcome';

/**
 * When true:
 * - Web: unauthenticated users land on Bloom Universal hub (`/universal`)
 *   via `/` (see `app/index.tsx`) and after logout / session expire
 * - Native: app launch shows Bloom Experience landing (`BloomExperienceScreen`)
 * Bloom Universal routes remain guest-only either way.
 */
export const ENABLE_BLOOM_UNIVERSAL_LANDING = true;

/** Must match `BLOOM_UNIVERSAL_HUB_PATH` (avoid importing modules into core). */
const BLOOM_UNIVERSAL_LANDING_PATH = '/universal';

/**
 * First-paint / splash destination for logged-out users (web + shared redirects).
 * - Flag on → Bloom Universal hub path (web `/` redirects here; splash may use it)
 * - Flag off → `/welcome`
 * Native launch entry uses `BloomExperienceScreen` when the flag is on — see `app/index.tsx`.
 * Post-logout / auth-guard on native always uses `WELCOME_LOGIN_PATH` (web-only Universal).
 */
export function getUnauthenticatedLandingPath(): string {
  if (ENABLE_BLOOM_UNIVERSAL_LANDING) {
    return BLOOM_UNIVERSAL_LANDING_PATH;
  }
  return '/welcome';
}

/**
 * @deprecated Prefer runtime `useIsAdhaJourney()` / `getIsAdhaJourney()` from the app store.
 * Static default is always false — ADHA mode is enabled after the UAE Pass / ADHA login path.
 */
export const ENABLE_ADHA_JOURNEY = false;

/**
 * Master switch for viewport-fit UI scaling on web.
 * Also requires `enableWebScaling` = `"true"` in the standard Contentful locales fetch.
 * Set to false to disable entirely regardless of Contentful.
 */
export const ENABLE_DESKTOP_FONT_SCALING = true;

/**
 * Guest catalogue filter.
 * When true, the guest listing (and explore list) only includes projects that
 * have at least one Contentful image. Projects missing from Contentful, or
 * present with an empty gallery, are hidden.
 * When false, every project from GET /v1/projects is shown.
 */
export const showGuestProjectWithImageOnly = true;

/** SSR fallback width when window is unavailable. */
export const DESKTOP_SCALING_DESIGN_MAX_WIDTH = 1890;

/**
 * Viewport at/below which UI uses minimum scale (0.8 = 12.8px / 16px rem ratio).
 * Between MIN and MAX widths, scale is fluid.
 */
export const DESKTOP_SCALING_DESIGN_MIN_WIDTH = 1512;

/** Minimum UI scale on the design curve (12.8 / 16). */
export const DESKTOP_SCALING_MIN_SCALE = 0.8;

/** Hard floor — allows full compensation at 200% browser zoom (1/2 = 0.5). */
export const DESKTOP_SCALING_ABSOLUTE_MIN = 0.5;

/**
 * Only apply fit-scale at this CSS viewport width and above.
 * Phone and iPad (including iPad 10 landscape at 1180) use the responsive
 * layout. Desktop zoom below this crops those layouts and locks scrolling.
 */
export const DESKTOP_SCALING_MIN_VIEWPORT = 1280;
